import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import NotificationBell from '../components/notifications/NotificationBell';
import CardCapsule from '../components/ui/CardCapsule';

const CATEGORY_TABS = [
  { id: 'all', label: 'All' },
  { id: 'Play', label: 'Play' },
  { id: 'Move', label: 'Move' },
  { id: 'Mind', label: 'Mind' },
];

const FILTER_TABS = [
  { id: 'sort', label: 'Sort' },
  { id: 'type', label: 'Type' },
  { id: 'status', label: 'Status' },
  { id: 'price', label: 'Price' },
];

const SORT_OPTIONS = [
  { id: 'popularity', label: 'Popularity' },
  { id: 'date', label: 'Date' },
  { id: 'price_low', label: 'Price: Low to High' },
  { id: 'price_high', label: 'Price: High to Low' },
];

const TYPE_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'Play', label: 'Play' },
  { id: 'Move', label: 'Move' },
  { id: 'Mind', label: 'Mind' },
];

const STATUS_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'Planned', label: 'Planned' },
  { id: 'Ongoing', label: 'Ongoing' },
  { id: 'Completed', label: 'Completed' },
];

const PRICE_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'free', label: 'Free' },
  { id: 'paid', label: 'Paid' },
];

const eventPalette = {
  Play: { bg: '#F4D341', accent: '#2B3A67', icon: 'ph-soccer-ball' },
  Move: { bg: '#E54562', accent: '#FFFFFF', icon: 'ph-person-simple-run' },
  Mind: { bg: '#2B3A67', accent: '#F4D341', icon: 'ph-brain' },
  default: { bg: '#3AAFA9', accent: '#FFFFFF', icon: 'ph-microphone-stage' },
};

const DEFAULT_RADIUS_KM = 5;

// Short "Area, City" label for the location card (e.g. "Kandivali East,
// Mumbai") instead of a full postal address.
const AREA_TYPES = ['sublocality_level_1', 'neighborhood', 'sublocality', 'sublocality_level_2'];
function shortPlaceLabel({ components = [], formatted = '', city = '', name = '' }) {
  const pick = (types) => components.find((c) => types.some((t) => c.types?.includes(t)))?.long_name;
  const cityName = city || pick(['locality']) || pick(['administrative_area_level_2']) || '';
  let area = pick(AREA_TYPES);
  if (!area && formatted && cityName) {
    // From a formatted address: the part just before the city, unless it's
    // a house number / plot.
    const parts = formatted.split(',').map((x) => x.trim());
    const i = parts.indexOf(cityName);
    if (i > 0 && !/\d/.test(parts[i - 1])) area = parts[i - 1];
  }
  if (!area && name && name !== cityName && name.split(/\s+/).length <= 3) area = name;
  if (area && cityName && area !== cityName) return `${area}, ${cityName}`;
  return area || cityName || name || formatted;
}

function PlacesAutocompleteInput({ onPlaceSelect }) {
  const inputRef = useRef(null);
  const placesLibrary = useMapsLibrary('places');

  useEffect(() => {
    if (!placesLibrary || !inputRef.current) return;

    const autocomplete = new placesLibrary.Autocomplete(inputRef.current, {
      fields: ['geometry', 'formatted_address', 'name', 'address_components'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place?.geometry?.location) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      onPlaceSelect({
        lat,
        lng,
        label: shortPlaceLabel({
          components: place.address_components,
          formatted: place.formatted_address,
          name: place.name,
        }) || 'Selected location',
        fullAddress: place.formatted_address || place.name || '',
      });
    });
  }, [placesLibrary, onPlaceSelect]);

  return (
    <input
      ref={inputRef}
      data-testid="events-location-input"
      placeholder="Search a location"
      className="w-full text-sm focus:outline-none text-[#2B3A67]"
    />
  );
}

export default function EventsPageV2() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState('price');
  const [sortOption, setSortOption] = useState('popularity');
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [showLocationBar, setShowLocationBar] = useState(true);
  const lastScrollYRef = useRef(0);
  const [userCoords, setUserCoords] = useState(null);
  const [locationLabel, setLocationLabel] = useState('Detecting location...');
  const [locationFull, setLocationFull] = useState('');
  const [locationError, setLocationError] = useState('');
  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  const fetchEvents = useCallback(
    async (coords = null) => {
      try {
        setLoading(true);
        const params = coords
          ? {
              lat: coords.lat,
              lng: coords.lng,
              radius: DEFAULT_RADIUS_KM,
            }
          : undefined;
        const response = await axiosInstance.get('/events', { params });
        setEvents(response.data);
      } catch (err) {
        toast.error('Failed to load events');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reverseGeocode = useCallback(async (coords) => {
    try {
      const response = await axiosInstance.post('/locations/reverse-geocode', {
        latitude: coords.lat,
        longitude: coords.lng,
      });
      const { city, formatted_address: formatted } = response.data;
      const short = shortPlaceLabel({ formatted, city });
      setLocationLabel(short ? `Near ${short}` : 'Near you');
      setLocationFull(formatted || '');
    } catch (error) {
      setLocationLabel('Near you');
      setLocationFull('');
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    const detectLocation = () => {
      if (!navigator.geolocation) {
        setLocationError('Location unavailable');
        setLocationLabel('Set your location');
        return;
      }

      setLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserCoords(coords);
          setLocationError('');
          await reverseGeocode(coords);
          setLocationLoading(false);
        },
        () => {
          setLocationError('Location permission denied');
          setLocationLabel('Set your location');
          setLocationLoading(false);
        }
      );
    };

    detectLocation();
  }, [reverseGeocode]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollYRef.current + 10) {
        setShowLocationBar(false);
      } else if (currentScrollY < lastScrollYRef.current - 10) {
        setShowLocationBar(true);
      }
      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (userCoords) {
      fetchEvents(userCoords);
    }
  }, [userCoords, fetchEvents]);

  const filteredEvents = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filtered = events
      .filter((event) => {
        if (!normalizedQuery) return true;
        const haystack = `${event.title} ${event.type} ${event.venue?.name || ''} ${event.venue?.city || ''}`.toLowerCase();
        return haystack.includes(normalizedQuery);
      })
      .filter((event) => (activeCategory === 'all' ? true : event.type === activeCategory))
      .filter((event) => (statusFilter === 'all' ? true : event.event_status === statusFilter))
      .filter((event) => (upcomingOnly ? event.event_status !== 'Completed' : true))
      .filter((event) => {
        if (priceFilter === 'all') return true;
        const minPrice = getMinPriceValue(event);
        return priceFilter === 'free' ? minPrice === 0 : minPrice > 0;
      });

    if (sortOption === 'date') {
      return [...filtered].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    }
    if (sortOption === 'price_low') {
      return [...filtered].sort((a, b) => getMinPriceValue(a) - getMinPriceValue(b));
    }
    if (sortOption === 'price_high') {
      return [...filtered].sort((a, b) => getMinPriceValue(b) - getMinPriceValue(a));
    }
    return filtered;
  }, [events, activeCategory, statusFilter, priceFilter, searchQuery, sortOption, upcomingOnly]);

  // The Status filter "Completed" switches this list to past events.
  const showingCompleted = statusFilter === 'Completed';
  const upcomingEvents = filteredEvents.filter((event) => {
    const startTime = new Date(event.start_time);
    if (Number.isNaN(startTime.getTime())) return false;
    // Otherwise show everything not yet completed (upcoming OR in progress). The
    // backend decides: simple events complete at their end date, tournaments only
    // once an admin completes them — so a tournament running late stays visible.
    return showingCompleted || event.event_status !== 'Completed';
  });
  const heroEvent = upcomingEvents[0];
  // Anything narrowing the list? "See All" clears it back to every event.
  const listNarrowed = activeCategory !== 'all' || statusFilter !== 'all' || priceFilter !== 'all'
    || upcomingOnly || searchQuery.trim() !== '';
  const clearFilters = () => {
    setActiveCategory('all');
    setStatusFilter('all');
    setPriceFilter('all');
    setSortOption('popularity');
    setUpcomingOnly(false);
  };
  const seeAll = () => {
    clearFilters();
    setSearchQuery('');
  };
  const restEvents = upcomingEvents.slice(1);

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

  const formatTime = (dateStr) =>
    new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  const getMinPriceValue = (event) => {
    // Tournaments: the lowest fee among the registrations they take (paise)
    if (event.registration_price) return (event.registration_price.min || 0) / 100;
    if (!event.ticket_tiers?.length) return 0;
    return Math.min(...event.ticket_tiers.map((tier) => tier.price));
  };

  const formatPrice = (event) => {
    const minPrice = getMinPriceValue(event);
    if (minPrice === 0) return 'Free';
    const rp = event.registration_price;
    // Different fees for different registrations → "From ₹500"
    return rp && rp.max > rp.min ? `From ${formatCurrency(minPrice)}` : formatCurrency(minPrice);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-sand flex items-center justify-center pb-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]"></div>
      </div>
    );
  }

  const isLocationBarVisible = showLocationBar || showLocationSearch;

  return (
    <div
      className={`min-h-screen bg-gray-50 pb-nav ${isLocationBarVisible ? 'pt-20' : 'pt-6'} text-[#2B3A67]`}
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      <div
        className={`fixed top-0 left-0 right-0 z-40 pt-safe transition-transform duration-200 ${
          isLocationBarVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
        data-testid="events-location-bar"
      >
        <div className="bg-gray-50/90 backdrop-blur-md px-6 pt-2 pb-2">
          <div
            className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-2 flex flex-wrap items-center gap-3"
            data-testid="events-location-card"
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest text-gray-400">Location</p>
              <p data-testid="events-location-label" className="text-[13px] font-bold truncate" title={locationFull || undefined}>
                {locationLoading ? 'Detecting location...' : locationLabel}
              </p>
              {locationError && (
                <p data-testid="events-location-error" className="text-xs text-[#E54562] mt-1">
                  {locationError}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="events-location-detect"
                onClick={() => {
                  setLocationError('');
                  setLocationLabel('Detecting location...');
                  setLocationLoading(true);
                  if (!navigator.geolocation) {
                    setLocationError('Location unavailable');
                    setLocationLabel('Set your location');
                    setLocationLoading(false);
                    return;
                  }
                  navigator.geolocation.getCurrentPosition(
                    async (position) => {
                      const coords = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                      };
                      setUserCoords(coords);
                      await reverseGeocode(coords);
                      setLocationLoading(false);
                    },
                    () => {
                      setLocationError('Location permission denied');
                      setLocationLabel('Set your location');
                      setLocationLoading(false);
                    }
                  );
                }}
                className="px-3 py-1.5 rounded-lg bg-[#2B3A67] text-white text-[11px] font-bold"
              >
                Use my location
              </button>
              <button
                data-testid="events-location-search-toggle"
                onClick={() => {
                  setShowLocationSearch((prev) => !prev);
                  setShowLocationBar(true);
                }}
                className="px-3 py-1.5 rounded-lg bg-gray-100 text-[11px] font-bold"
              >
                Search
              </button>
            </div>
          </div>
          {showLocationSearch && (
            <div
              className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-2 mt-2"
              data-testid="events-location-search"
            >
              <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} libraries={['places']}>
                <PlacesAutocompleteInput
                  onPlaceSelect={(place) => {
                    setUserCoords({ lat: place.lat, lng: place.lng });
                    setLocationLabel(`Near ${place.label}`);
                    setLocationFull(place.fullAddress);
                    setShowLocationSearch(false);
                    setLocationError('');
                  }}
                />
              </APIProvider>
            </div>
          )}
        </div>
      </div>

      <header className="sticky top-0 z-30 bg-gray-50/90 backdrop-blur-md px-6 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gray-400 font-bold" data-testid="events-eyebrow">
              Backyard
            </p>
            <h1 className="text-3xl font-extrabold" data-testid="events-title">
              Connect
            </h1>
            <p className="text-sm text-gray-500">Curated events around the city.</p>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={() => setShowFilters(true)}
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md border border-gray-100"
              data-testid="events-filter-button"
            >
              <i className="ph-bold ph-sliders-horizontal text-xl text-[#2B3A67]"></i>
            </button>
          </div>
        </div>


        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 mb-4 flex items-center gap-3">
          <i className="ph-bold ph-magnifying-glass text-lg text-gray-400"></i>
          <input
            data-testid="events-search-input"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search events, venues, or categories"
            className="flex-1 text-sm focus:outline-none text-[#2B3A67]"
          />
          {searchQuery && (
            <button
              data-testid="events-search-clear"
              onClick={() => setSearchQuery('')}
              className="text-xs font-bold text-[#E54562]"
            >
              Clear
            </button>
          )}
        </div>

        <div className="flex gap-3 overflow-x-auto pb-3 no-scrollbar" data-testid="events-category-tabs">
          {CATEGORY_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveCategory(tab.id)}
              className={`px-5 py-2 rounded-full font-bold text-sm whitespace-nowrap ${
                activeCategory === tab.id
                  ? 'bg-[#2B3A67] text-white'
                  : 'bg-white text-[#2B3A67] border-2 border-gray-100'
              }`}
              data-testid={`events-category-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="px-6 pt-6 space-y-6">
        {heroEvent && (
          <section
            role="link"
            tabIndex={0}
            onClick={() => navigate(`/events/${heroEvent.id}`)}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/events/${heroEvent.id}`); }}
            className="rounded-[2rem] overflow-hidden shadow-lg border border-gray-100 cursor-pointer transition-transform active:scale-[0.99]"
            data-testid={`event-hero-${heroEvent.id}`}
          >
            {/* At least 11rem tall, growing with the title so a long one never
                pushes the tag and heart off the top. */}
            <div className="relative min-h-[11rem] flex" style={{ backgroundColor: '#2B3A67' }}>
              {heroEvent.image_url ? (
                <img 
                  src={heroEvent.image_url} 
                  alt={heroEvent.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="event-hero-image"
                />
              ) : (
                <div className="absolute inset-0 opacity-30" style={{
                  backgroundImage:
                    'repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(255,255,255,0.2) 10px, rgba(255,255,255,0.2) 20px)',
                }}></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              {/* Bottom padding keeps the title clear of the capsule. */}
              <div className="relative flex-1 flex flex-col justify-between gap-3 px-6 pt-5 pb-12 text-white">
                <div className="flex justify-between items-center">
                  <div className="bg-white/20 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest" data-testid="event-hero-tag">
                    Featured
                  </div>
                  <button className="text-white" data-testid="event-hero-like" onClick={(e) => e.stopPropagation()}>
                    <i className="ph-fill ph-heart text-xl"></i>
                  </button>
                </div>
                <h2 className="text-2xl font-extrabold leading-tight" data-testid="event-hero-title">
                  {heroEvent.title}
                </h2>
              </div>
              <CardCapsule
                position="bottom-3 right-3"
                label={heroEvent.event_status === 'Completed' ? 'View' : heroEvent.type === 'Play' ? 'Join' : 'Book'}
                testId="event-hero-cta"
              />
            </div>
            <div className="bg-white p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-500" data-testid="event-hero-date">
                    {formatDate(heroEvent.start_time)} • {formatTime(heroEvent.start_time)}
                  </p>
                  <p className="text-xs text-gray-400" data-testid="event-hero-location">
                    {heroEvent.venue?.city}
                  </p>
                </div>
                <span className="text-lg font-extrabold text-[#E54562]" data-testid="event-hero-price">
                  {formatPrice(heroEvent)}
                </span>
              </div>
            </div>
          </section>
        )}

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-extrabold">{showingCompleted ? 'Completed Events' : 'Upcoming Events'}</h2>
            {listNarrowed && (
              <button onClick={seeAll} className="text-sm font-bold text-[#E54562]" data-testid="events-view-all">
                See All
              </button>
            )}
          </div>

          <div className="space-y-4">
            {restEvents.length === 0 ? (
              <div className="bg-white rounded-2xl p-6 text-sm text-gray-500" data-testid="events-empty">
                No events found. Try another category.
              </div>
            ) : (
              restEvents.map((event) => {
                const palette = eventPalette[event.type] || eventPalette.default;
                return (
                  <div
                    key={event.id}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/events/${event.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/events/${event.id}`); }}
                    className="bg-white rounded-2xl p-4 shadow-md border border-gray-100 cursor-pointer transition-transform active:scale-[0.98]"
                    data-testid={`event-card-${event.id}`}
                  >
                    <div className="flex gap-4">
                      <div className="relative flex-shrink-0 w-24 h-24 rounded-2xl flex items-center justify-center text-white" style={{ backgroundColor: palette.bg }}>
                        <i className={`ph-duotone ${palette.icon} text-4xl`} style={{ color: palette.accent }}></i>
                        <CardCapsule
                          position="-bottom-2 -right-2"
                          size="sm"
                          label={event.event_status === 'Completed' ? 'View' : event.type === 'Play' ? 'Join' : 'Tickets'}
                          testId={`event-cta-${event.id}`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest" data-testid={`event-date-${event.id}`}>
                            {formatDate(event.start_time)} • {formatTime(event.start_time)}
                          </p>
                          <button className="text-gray-400" data-testid={`event-like-${event.id}`} onClick={(e) => e.stopPropagation()}>
                            <i className="ph-bold ph-heart"></i>
                          </button>
                        </div>
                        <h3 className="font-extrabold text-lg mt-1" data-testid={`event-title-${event.id}`}>
                          {event.title}
                        </h3>
                        <p className="text-xs text-gray-400 mt-1" data-testid={`event-location-${event.id}`}>
                          <i className="ph-fill ph-map-pin text-[#E54562]"></i> {event.venue?.name}
                        </p>
                        <p className="mt-3 text-sm font-extrabold text-[#E54562]" data-testid={`event-price-${event.id}`}>
                          {formatPrice(event)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </main>

      {showFilters && (
        <div
          className="fixed inset-0 bg-black/40 z-[1100] flex items-end"
          onClick={() => setShowFilters(false)}
          data-testid="events-filter-modal"
        >
          <div
            className="bg-white w-full rounded-t-3xl p-6 shadow-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-extrabold">Filter by</h3>
              <div className="flex items-center gap-4">
                <button
                  onClick={clearFilters}
                  className="text-sm font-semibold text-[#2B3A67]/70"
                  data-testid="events-filter-clear"
                >
                  Clear all
                </button>
                <button onClick={() => setShowFilters(false)} data-testid="events-filter-close">
                  <i className="ph-bold ph-x text-xl"></i>
                </button>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap mb-4" data-testid="events-filter-tabs">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setFilterTab(tab.id)}
                  className={`px-4 py-2 rounded-full text-sm font-bold ${
                    filterTab === tab.id ? 'bg-[#2B3A67] text-white' : 'bg-gray-100 text-[#2B3A67]'
                  }`}
                  data-testid={`filter-tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="space-y-3 mb-4">
              {filterTab === 'sort' &&
                SORT_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setSortOption(option.id)}
                    className={`w-full px-5 py-4 rounded-2xl flex items-center justify-between font-semibold ${
                      sortOption === option.id ? 'bg-[#2B3A67] text-white' : 'bg-gray-100 text-[#2B3A67]'
                    }`}
                    data-testid={`filter-sort-${option.id}`}
                  >
                    <span>{option.label}</span>
                    {sortOption === option.id && <span className="text-xs uppercase tracking-widest">Selected</span>}
                  </button>
                ))}
              {filterTab === 'type' &&
                TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setActiveCategory(option.id)}
                    className={`w-full px-5 py-4 rounded-2xl flex items-center justify-between font-semibold ${
                      activeCategory === option.id ? 'bg-[#2B3A67] text-white' : 'bg-gray-100 text-[#2B3A67]'
                    }`}
                    data-testid={`filter-type-${option.id}`}
                  >
                    <span>{option.label}</span>
                    {activeCategory === option.id && <span className="text-xs uppercase tracking-widest">Selected</span>}
                  </button>
                ))}
              {filterTab === 'status' &&
                STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setStatusFilter(option.id)}
                    className={`w-full px-5 py-4 rounded-2xl flex items-center justify-between font-semibold ${
                      statusFilter === option.id ? 'bg-[#2B3A67] text-white' : 'bg-gray-100 text-[#2B3A67]'
                    }`}
                    data-testid={`filter-status-${option.id.toLowerCase()}`}
                  >
                    <span>{option.label}</span>
                    {statusFilter === option.id && <span className="text-xs uppercase tracking-widest">Selected</span>}
                  </button>
                ))}
              {filterTab === 'price' &&
                PRICE_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setPriceFilter(option.id)}
                    className={`w-full px-5 py-4 rounded-2xl flex items-center justify-between font-semibold ${
                      priceFilter === option.id ? 'bg-[#2B3A67] text-white' : 'bg-gray-100 text-[#2B3A67]'
                    }`}
                    data-testid={`filter-price-${option.id}`}
                  >
                    <span>{option.label}</span>
                    {priceFilter === option.id && <span className="text-xs uppercase tracking-widest">Selected</span>}
                  </button>
                ))}
            </div>

            <button
              onClick={() => setUpcomingOnly((prev) => !prev)}
              className={`px-6 py-2 rounded-full text-sm font-semibold mb-4 ${
                upcomingOnly ? 'bg-[#2B3A67] text-white' : 'bg-gray-100 text-[#2B3A67]'
              }`}
              data-testid="events-filter-upcoming"
            >
              Upcoming only
            </button>

            <button
              onClick={() => setShowFilters(false)}
              className="w-full py-3 rounded-xl text-white font-bold bg-[#2B3A67]"
              data-testid="events-filter-apply"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}